import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Alert,
  Stack,
  Avatar,
  Card,
  Paper,
} from "@mui/material";
import LayoutContainer from "../widgets/LayoutContainer";
import useOpenRequest from "../../hooks/useOpenRequest";
import { API_ENDPOINT } from "../../utils/constants";
import MuPb from "../widgets/MuPb";
import { toastError } from "../../utils/util";
import axios from "axios";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";

const WelcomeVisitor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    isLoading: loading,
    data,
    isError,
    error: oError,
  } = useOpenRequest(`${API_ENDPOINT}user/check-barcode-id?id=${id}`);

  const { data: comD, isLoading: dLoading } = useOpenRequest(
    `${API_ENDPOINT}user/get-company-info?bid=${id}`
  );

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");

  // Simulated API call to send OTP
  const handleVerifyEmail = async () => {
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_ENDPOINT}user/email-verification-otp-request`,
        { email }
      );
      toast.success(response.data.message);
      setOtpToken(response.data.token);
      setOtpSent(true);
      setError("");
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  async function verifying() {
    try {
      setIsVerifying(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}user/verify-otp-email-visitor`,
        { otp, otpToken, bid: id }
      );
      toast.success(data.message);
      const token = data.token;
      setTimeout(() => {
        if (data.exists) {
          navigate(`/visitor-profile/${token}`);
        } else {
          navigate(`/visitor-create-profile/${token}`);
        }
      }, 500);
    } catch (error) {
      toastError(error);
    } finally {
      setIsVerifying(false);
    }
  }

  if (loading || dLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{oError?.message}</Alert>;
  }

  return (
    <LayoutContainer>
      {/* Banner and Logo Section */}

      <Paper sx={{ maxWidth: "450px", mx: "auto" }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",

            height: 150, // Fixed height for 3:1 banner

            mt: 2,
            mb: -8, // Overlap with the content box
          }}
        >
          {comD?.settings?.bnLogo && (
            <Box
              component="img"
              src={comD.settings.bnLogo}
              alt={`${comD?.company} banner`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px 8px 0 0",
                boxShadow: 2,
              }}
            />
          )}
          {comD?.settings?.srLogo && (
            <Avatar
              src={comD.settings.srLogo}
              alt={`${comD?.company} logo`}
              sx={{
                width: 120,
                height: 120,
                position: "absolute",
                bottom: -60, // Half of the avatar hangs below the banner
                left: "50%",
                transform: "translateX(-50%)",
                border: "4px solid white",
                bgcolor: "grey.100",
                boxShadow: 3,
              }}
            />
          )}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          mx="auto"
          mt={8} // Adjusted to account for banner overlap
          textAlign="center"
          p={3}
          borderRadius={2}
          bgcolor="white"
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary"
            gutterBottom
            sx={{ mt: 6 }}
          >
            Welcome to {comD?.company}
          </Typography>

          <Typography variant="body1" color="textSecondary" mb={3}>
            Please enter your email to receive an OTP for verification.
          </Typography>

          {/* Email Input */}
          <TextField
            label="Email Address"
            type="email"
            variant="outlined"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isEmailVerified}
            error={!!error}
            helperText={error}
            sx={{ mb: 2 }}
          />

          {/* Verify Button */}
          {!otpSent && (
            <LoadingButton
              size="small"
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleVerifyEmail}
              loading={isLoading}
              sx={{ borderRadius: 20, py: 1.5 }}
            >
              Proceed
            </LoadingButton>
          )}

          {/* OTP Sent Confirmation */}
          {otpSent && (
            <Alert severity="success" sx={{ mt: 2, width: "100%" }}>
              OTP has been sent to your email.
            </Alert>
          )}

          {/* OTP Input Field (Shown after verification) */}
          {otpSent && (
            <Stack direction="column" gap={2} width="100%" mt={2}>
              <TextField
                label="Enter OTP"
                type="number"
                size="small"
                variant="outlined"
                fullWidth
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              <LoadingButton
                loading={isVerifying}
                onClick={verifying}
                variant="contained"
                size="small"
                fullWidth
                sx={{ borderRadius: 20, py: 1.5 }}
              >
                Verify
              </LoadingButton>
            </Stack>
          )}
        </Box>
      </Paper>
    </LayoutContainer>
  );
};

export default WelcomeVisitor;
